export interface WeekEntry {
  kind: "week";
  number: 1 | 2 | 3 | 4;
  window: string;
  title: string;
  focus: string;
  gate: string;
  deliverables: string[];
  conclusions: string[] | null;
}

export type CheckpointTone = "transition" | "build";

export interface CheckpointEntry {
  kind: "checkpoint";
  id: string;
  tone: CheckpointTone;
  date: string;
  title: string;
  outcome: string;
  bullets: string[];
}

export type TimelineEntry = WeekEntry | CheckpointEntry;

const WEEK_RANGES: Array<{ number: 1 | 2 | 3 | 4; start: string; end: string }> = [
  { number: 1, start: "2026-05-04", end: "2026-05-10" },
  { number: 2, start: "2026-05-11", end: "2026-05-17" },
  { number: 3, start: "2026-05-18", end: "2026-05-24" },
  { number: 4, start: "2026-05-25", end: "2026-05-31" },
];

export function getCurrentWeekNumber(now: Date = new Date()): 1 | 2 | 3 | 4 | null {
  const today = now.toISOString().slice(0, 10);
  for (const { number, start, end } of WEEK_RANGES) {
    if (today >= start && today <= end) return number;
  }
  return null;
}

export const timelineV2: TimelineEntry[] = [
  {
    kind: "week",
    number: 1,
    window: "May 4 – May 10",
    title: "Mapping current systems — part 1",
    focus:
      "Discover and document how Yandex's PIM, supply chain and B2B architecture work today, and where they break for the Israeli B2B model.",
    gate: "Current-state architecture understood end-to-end for supply chain + PIM.",
    deliverables: [
      "High-level architecture diagram",
      "System integration map",
      "Dependency tracker with owners",
    ],
    conclusions: [
      "Hybrid B2B architecture locked — new independent B2B stack with its own PIM, Woody (ERP) kept for supplier ordering.",
      "Yandex PIM disqualified for B2B — cannot manage inventory by batch / expiration dates (supermarkets require 30-day minimum shelf life).",
      "Six PIM gaps surfaced: no GS1 integration, no cluster modelling, duplicated per-language fields, weak search, no staging environment, overly-granular permissions.",
      "Replace-all-Yandex assumption restated by Maor — creates tension with the May 5 hybrid decision; flagged as open.",
    ],
  },
  {
    kind: "week",
    number: 2,
    window: "May 11 – May 17",
    title: "Mapping current systems — part 2",
    focus:
      "Map the operational, analytics, pricing, content and warehouse layers — find the load-bearing blockers.",
    gate: "All major system domains mapped; load-bearing blockers named.",
    deliverables: [
      "Domain map (BI, Pricing, CMS, WMS, Delivery)",
      "Gap analysis per domain",
      "Blocker list with severity",
    ],
    conclusions: [
      "CRM absence is the top blocker — no Yandex Passport in Israel; without a customer-identity layer, BI / segmentation / loyalty are all structurally constrained.",
      "Yango's 0–2% missing-items rate (scan-to-shelf + cycle counts) is the competitive moat any replacement WMS must preserve.",
      "~200 sqm stores cannot absorb B2B order sizes — options: dedicated dark-store warehouse vs Netanya-style 500–600 sqm store with split inventory (open).",
      "Pricing: upstream manual workflow loses discount-breakdown detail; mixed-price bundles unsupported; coupon admin is raw JSON.",
      "Content: Admin Panel / Adminca split is a pain point; banner/button/story desync; content team dev-blocked on Russia side (Leoni).",
    ],
  },
  {
    kind: "checkpoint",
    id: "owner-checkpoint",
    tone: "transition",
    date: "May 13",
    title: "Owner checkpoint — locked direction",
    outcome: "Build B2B from scratch — no Yandex component reuse.",
    bullets: [
      "Two roadmaps presented to owner: reuse Yandex vs build from scratch.",
      "Reuse-Yandex fork dropped; from-scratch path adopted for the B2B.",
      "Downstream effect — Week 3 pivots from current-state mapping to B2B MVP spec work.",
    ],
  },
  {
    kind: "week",
    number: 3,
    window: "May 18 – May 24",
    title: "B2B MVP refinement",
    focus:
      "Translate the from-scratch decision into a concrete B2B MVP — what we ship, how money flows, how customers come on board.",
    gate: "MVP scope and B2B onboarding flow agreed by owner + product.",
    deliverables: [
      "New ecosystem MVP feature list",
      "Payment flow specification",
      "Invoicing integration spec",
      "Onboarding B2B users spec",
    ],
    conclusions: null,
  },
  {
    kind: "week",
    number: 4,
    window: "May 25 – May 31",
    title: "Build approach & MVP board refinement",
    focus:
      "Compare off-the-shelf components for composing the ecosystem; refine MVP board verdicts; produce the development proposal.",
    gate: "MVP board verdicts final; development proposal accepted.",
    deliverables: [
      "Final technical specification",
      "Development proposal with timeline and cost",
      "Risk assessment for development phase",
      "Post-MVP feature roadmap",
    ],
    conclusions: null,
  },
  {
    kind: "checkpoint",
    id: "estimation-refresh",
    tone: "build",
    date: "Jun 1",
    title: "Estimation refresh — assessment & implementation",
    outcome: "Refine assessment runway and the 3-month B2B implementation horizon.",
    bullets: [
      "Decide whether the 4-week discovery + spec phase needs an extension before development kicks off.",
      "Revisit the originally-scoped 3-month B2B implementation timeline — confirm or adjust against the new MVP feature list, integration specs, and risk assessment.",
      "Lock the final development proposal (timeline + cost) and post-MVP roadmap before handing off to the build team.",
    ],
  },
];
